import './StatsTable.sass';

import { FC, useEffect, useMemo, useState } from 'react';
import format from 'date-fns/format';
import { fromUnixTime } from 'date-fns';

import { classNames } from '$utils';
import { CurrencyEnum } from '$store/models';
import { Button } from '$uikit';
import { useTranslator } from '$hooks';

import type { IncomeType } from '../StatisticsPro.types';

export type SortKey = 'date' | 'total' | 'others' | IncomeType;
export type SortDir = 'asc' | 'desc';

export interface StatsTableProps {
  days: number[];
  values: Record<number, Partial<Record<IncomeType, number>>>;
  selected: IncomeType[];
  othersTypes: IncomeType[];
  labels: Record<string, string>;
  currency: CurrencyEnum;
  onExport: () => void;
  integer?: boolean;
  totalLabel?: string;
}

interface TableRow {
  date: number;
  cells: number[];
  othersValue: number;
  total: number;
}

export const StatsTable: FC<StatsTableProps> = ({
  days,
  values,
  selected,
  othersTypes,
  labels,
  currency,
  onExport,
  integer = false,
  totalLabel,
}) => {
  const t = useTranslator();
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const hasOthers = othersTypes.length > 0;
  const othersLabel = t(
    selected.length === 0 ? 'statistics_pro_all' : 'statistics_pro_others',
  );

  useEffect(() => {
    if (sortKey === 'date' || sortKey === 'total') return;
    if (sortKey === 'others' && !hasOthers) {
      setSortKey('date');
      setSortDir('desc');
      return;
    }
    if (sortKey !== 'others' && !selected.includes(sortKey as IncomeType)) {
      setSortKey('date');
      setSortDir('desc');
    }
  }, [selected, hasOthers, sortKey]);

  const rows: TableRow[] = useMemo(() => {
    const base = days
      .map<TableRow>((d) => {
        const cells = selected.map((cat) => values[d]?.[cat] ?? 0);
        const othersValue = othersTypes.reduce(
          (sum, t) => sum + (values[d]?.[t] ?? 0),
          0,
        );
        const total = cells.reduce((a, b) => a + b, 0) + othersValue;
        return { date: d, cells, othersValue, total };
      })
      .filter((row) => row.total !== 0);

    const valueFor = (row: TableRow): number => {
      if (sortKey === 'date') return row.date;
      if (sortKey === 'total') return row.total;
      if (sortKey === 'others') return row.othersValue;
      const idx = selected.indexOf(sortKey as IncomeType);
      return idx === -1 ? 0 : row.cells[idx];
    };

    const sorted = [...base].sort((a, b) => {
      const diff = valueFor(a) - valueFor(b);
      return sortDir === 'asc' ? diff : -diff;
    });
    return sorted;
  }, [days, selected, othersTypes, values, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDir('desc');
  };

  const arrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const ccy = currency.toUpperCase();
  const fmt = (n: number) =>
    integer ? String(Math.round(n)) : (Math.round(n * 100) / 100).toFixed(2);

  const dateLabel = (d: number) => format(fromUnixTime(d), 'd MMM yyyy');

  return (
    <div className="StatsTable">
      <div className="StatsTable__head">
        <div className="StatsTable__title">{t('statistics_pro_breakdown')}</div>
        <Button
          mode="inline"
          size="extra-small"
          onClick={onExport}
          disabled={rows.length === 0 || (selected.length === 0 && !hasOthers)}
        >
          {t('statistics_pro_export_csv')}
        </Button>
      </div>

      <div className="StatsTable__scroll">
        <table className="StatsTable__table">
          <thead>
            <tr>
              <th
                className={classNames('StatsTable__th', 'sortable')}
                onClick={() => toggleSort('date')}
              >
                {t('statistics_pro_col_date')}
                {arrow('date')}
              </th>
              {selected.map((cat) => (
                <th
                  key={cat}
                  className={classNames('StatsTable__th', 'sortable')}
                  onClick={() => toggleSort(cat)}
                >
                  {labels[cat] ?? cat}
                  {arrow(cat)}
                </th>
              ))}
              {hasOthers && (
                <th
                  className={classNames('StatsTable__th', 'sortable')}
                  onClick={() => toggleSort('others')}
                >
                  {othersLabel}
                  {arrow('others')}
                </th>
              )}
              <th
                className={classNames('StatsTable__th', 'sortable')}
                onClick={() => toggleSort('total')}
              >
                {totalLabel ?? t('statistics_pro_col_total', { currency: ccy })}
                {arrow('total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 || (selected.length === 0 && !hasOthers) ? (
              <tr>
                <td
                  className="StatsTable__empty"
                  colSpan={Math.max(2, selected.length + (hasOthers ? 1 : 0) + 2)}
                >
                  {t('statistics_pro_no_data_filters')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.date}>
                  <td className="StatsTable__td">{dateLabel(row.date)}</td>
                  {row.cells.map((v, i) => (
                    <td
                      key={`${row.date}-${selected[i]}`}
                      className="StatsTable__td numeric"
                    >
                      {fmt(v)}
                    </td>
                  ))}
                  {hasOthers && (
                    <td className="StatsTable__td numeric">{fmt(row.othersValue)}</td>
                  )}
                  <td className="StatsTable__td numeric total">{fmt(row.total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
